/**
 * Silent session refresh shared by useTokenRefresh and baseQueryWithReauth.
 *
 * Single flight: concurrent callers share one in-flight request, so a rotated refresh token
 * is never sent twice. The result tells the caller what to do:
 * - success: the store already holds the new tokens
 * - unauthorized: refresh token missing or rejected (HTTP 400, 401, 403), the caller logs out;
 *   fail-closed, because a rejected token never recovers and the session would otherwise
 *   stay open with every request returning 401. sessionRefreshFailed is not set
 * - transient: network error, timeout, invalid body or 5xx; the session is kept and
 *   sessionRefreshFailed is set
 * - stale: the session changed during the call (logout, another login); nothing was applied
 *   and callers must do nothing
 *
 * The result is applied only while the store still holds the refresh token that was sent,
 * and single flight is keyed by that token.
 */

import type { Dispatch } from '@reduxjs/toolkit';
import type { RootState } from '../../../App/store';
import { resolveAuthApiUrl } from '../../../Api/apiUrl';
import {
  setSessionRefreshFailed,
  tokensAdopted,
  tokensRefreshed,
  type TokensAdoptedPayload,
} from '../slices/authSlice';
import type { TokensRefreshedPayload, UserInfo } from '../types/auth.types';

const AUTH_API_URL = resolveAuthApiUrl(process.env.REACT_APP_API_URL);
const REFRESH_TIMEOUT_MS = 10_000;
const LOCK_NAME = 'travelcrm-auth-refresh';
const LOCK_WAIT_TIMEOUT_MS = 15_000;
const REJECTED_STATUSES = [400, 401, 403];

export type RefreshSessionResult = 'success' | 'unauthorized' | 'transient' | 'stale';

export interface RefreshSessionDeps {
  dispatch: Dispatch;
  getState: () => unknown;
}

let inFlight: { sentToken: string | null; promise: Promise<RefreshSessionResult> } | null = null;

const currentRefreshToken = (deps: RefreshSessionDeps): string | null =>
  (deps.getState() as RootState).auth.refreshToken;

const isSameSession = (deps: RefreshSessionDeps, sentToken: string): boolean => {
  const auth = (deps.getState() as RootState).auth;
  // A restoring store (page load with an expired access token) is a session too
  return (auth.isAuthenticated || auth.sessionRestoring === true) && auth.refreshToken === sentToken;
};

const isValidPayload = (data: unknown): data is TokensRefreshedPayload => {
  const payload = data as Partial<TokensRefreshedPayload> | null;
  return (
    !!payload &&
    typeof payload.accessToken === 'string' &&
    payload.accessToken.length > 0 &&
    !!payload.user &&
    typeof payload.user === 'object' &&
    typeof payload.expiresIn === 'number' &&
    Number.isFinite(payload.expiresIn)
  );
};

const markTransient = (deps: RefreshSessionDeps, sentToken: string): RefreshSessionResult => {
  if (!isSameSession(deps, sentToken)) {
    return 'stale';
  }
  deps.dispatch(setSessionRefreshFailed(true));
  return 'transient';
};

const requestRefresh = async (
  deps: RefreshSessionDeps,
  refreshToken: string | null
): Promise<RefreshSessionResult> => {
  if (!refreshToken) {
    return 'unauthorized';
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

  try {
    const response = await fetch(`${AUTH_API_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
      signal: controller.signal,
    });

    if (REJECTED_STATUSES.includes(response.status)) {
      return isSameSession(deps, refreshToken) ? 'unauthorized' : 'stale';
    }
    if (!response.ok) {
      return markTransient(deps, refreshToken);
    }

    const data: unknown = await response.json();
    if (!isSameSession(deps, refreshToken)) {
      return 'stale';
    }
    if (!isValidPayload(data)) {
      return markTransient(deps, refreshToken);
    }
    deps.dispatch(tokensRefreshed(data));
    return 'success';
  } catch {
    // Network error, timeout (abort) or unreadable body: keep the session
    return markTransient(deps, refreshToken);
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Reads the complete session persisted by login or refresh, or null when absent, incomplete
 * or unreadable (storage may throw).
 */
const readStoredSession = (): TokensAdoptedPayload | null => {
  try {
    const accessToken = localStorage.getItem('authToken');
    const refreshToken = localStorage.getItem('authRefreshToken');
    const userJson = localStorage.getItem('authUser');
    const expiresAtRaw = localStorage.getItem('authTokenExpiresAt');
    const tokenExpiresAt = Number(expiresAtRaw);
    // A missing or zero expiry would silently switch off the proactive refresh
    const hasExpiry = !!expiresAtRaw && Number.isFinite(tokenExpiresAt) && tokenExpiresAt > 0;
    if (!accessToken || !refreshToken || !userJson || !hasExpiry) {
      return null;
    }
    const user = JSON.parse(userJson) as UserInfo | null;
    if (!user || typeof user.id !== 'number') {
      return null;
    }
    return {
      accessToken,
      refreshToken,
      user,
      tokenExpiresAt,
      passwordChangeRequired: localStorage.getItem('authPasswordChangeRequired') === 'true',
    };
  } catch {
    return null;
  }
};

/**
 * Another tab sharing this localStorage session may already have rotated the refresh token.
 * Sending the old one again would make the backend revoke the whole token family, so the
 * stored session is adopted instead of calling the network.
 */
const refreshIfNotRotated = async (
  deps: RefreshSessionDeps,
  sentToken: string
): Promise<RefreshSessionResult> => {
  if (!isSameSession(deps, sentToken)) {
    return 'stale';
  }
  const stored = readStoredSession();
  if (!stored || stored.refreshToken === sentToken) {
    return requestRefresh(deps, sentToken);
  }
  deps.dispatch(tokensAdopted(stored));
  return isSameSession(deps, stored.refreshToken) ? 'success' : 'stale';
};

/**
 * Serializes refreshes across tabs with Web Locks where available. The storage is re-read
 * inside the lock because the previous holder may have just rotated the token. A lock held by
 * a hung tab must not block forever: the wait is aborted and reported as transient.
 */
const refreshAcrossTabs = async (
  deps: RefreshSessionDeps,
  sentToken: string
): Promise<RefreshSessionResult> => {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  if (!locks) {
    return refreshIfNotRotated(deps, sentToken);
  }

  const controller = new AbortController();
  const waitTimeoutId = setTimeout(() => controller.abort(), LOCK_WAIT_TIMEOUT_MS);
  try {
    return await locks.request(LOCK_NAME, { signal: controller.signal }, () => {
      clearTimeout(waitTimeoutId);
      return refreshIfNotRotated(deps, sentToken);
    });
  } catch {
    // Lock request rejected or aborted while waiting
    return markTransient(deps, sentToken);
  } finally {
    clearTimeout(waitTimeoutId);
  }
};

const runRefresh = (
  deps: RefreshSessionDeps,
  sentToken: string | null
): Promise<RefreshSessionResult> =>
  sentToken ? refreshAcrossTabs(deps, sentToken) : Promise.resolve('unauthorized');

/**
 * Refreshes the session using the refresh token currently held in the store. Never rejects.
 */
export const refreshSession = (deps: RefreshSessionDeps): Promise<RefreshSessionResult> => {
  const sentToken = currentRefreshToken(deps);
  if (inFlight && inFlight.sentToken === sentToken) {
    return inFlight.promise;
  }

  const entry = {
    sentToken,
    promise: runRefresh(deps, sentToken)
      .catch((): RefreshSessionResult => 'transient')
      .finally(() => {
        if (inFlight === entry) {
          inFlight = null;
        }
      }),
  };
  inFlight = entry;
  return entry.promise;
};
